import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { Alert, Button, ConfigProvider, Form, Input, Modal, Space, Table, Tag, Typography } from "antd";
import container from "../../../../../lib/di/container";
import AuthRepo from "../../../../../core/repos/auth.repo";
import { ElectronicContractRepo } from "../../../../../core/repos/tables/electronic-contract.repo";
import { MODAL_THEME } from "../../../../../core/themes/modal.theme";
import type { DemoElectronicContractBootstrapModel, ElectronicContractTemplateModel } from "../../../../../core/models/electronic-contract.model";
import { documentTypeLabels, getTemplateVariant } from "../../../../../core/utils/electronic-contract";
import { canBootstrapDemoTemplates, getBootstrapOutcome, validateDemoTemplateConfiguration } from "./template-configuration.helpers";

const authRepo = container.get(AuthRepo);
const electronicContractRepo = container.get(ElectronicContractRepo);

interface TemplateConfigurationModalProps {
  isModalOpen: boolean;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
  onSuccess?: () => void;
}

export const TemplateConfigurationModal: React.FC<TemplateConfigurationModalProps> = ({ isModalOpen, setIsModalOpen, onSuccess }) => {
  const user = authRepo.useDetails();
  const claims = authRepo.useClaims();
  const [form] = Form.useForm<DemoElectronicContractBootstrapModel>();
  const [templates, setTemplates] = useState<ElectronicContractTemplateModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState("");
  const isAdmin = canBootstrapDemoTemplates(claims, user?.userName);
  const isUat = process.env.REACT_APP_ECONTRACT_ENVIRONMENT === "uat";
  const isProduction = process.env.REACT_APP_ECONTRACT_ENVIRONMENT === "production";

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await electronicContractRepo.getTemplates("");
      if (response?.code !== "00" || !Array.isArray(response.result)) {
        setTemplates([]);
        setError(response?.errorMessage || "Không tải được danh sách mẫu của đơn vị hiện tại.");
        return;
      }
      setTemplates(response.result.filter(template => !template.waterFactoryId || template.waterFactoryId === user?.waterFactoryId));
    } catch {
      setTemplates([]);
      setError("Không tải được danh sách mẫu. Vui lòng kiểm tra kết nối.");
    } finally { setLoading(false); }
  }, [user?.waterFactoryId]);

  useEffect(() => {
    if (isModalOpen) { setError(""); setSummary(""); void loadTemplates(); }
  }, [isModalOpen, loadTemplates]);

  const bootstrap = async (values: DemoElectronicContractBootstrapModel) => {
    if (!isAdmin || !isUat) return;
    setSaving(true);
    setError("");
    setSummary("");
    try {
      const outcome = getBootstrapOutcome(await electronicContractRepo.bootstrapDemoTemplates(values));
      if (!outcome.success) { setError(outcome.error); return; }
      setSummary(`Đã tạo ${outcome.created} mẫu; ${outcome.skipped} mẫu đã có được giữ nguyên.`);
      await loadTemplates();
      onSuccess?.();
    } catch {
      setError("Chưa xác định được kết quả khởi tạo. Tải lại danh sách và kiểm tra trước khi thực hiện tiếp.");
    } finally { setSaving(false); }
  };

  const confirmBootstrap = (input: DemoElectronicContractBootstrapModel) => {
    if (!isAdmin || !isUat || saving) return;
    const values = {
      connectionProfile: input.connectionProfile?.trim() || "",
      companyEmail: input.companyEmail?.trim() || "",
      surveyEmail: input.surveyEmail?.trim() || "",
    };
    const errors = validateDemoTemplateConfiguration(values);
    if (Object.keys(errors).length) {
      form.setFields((Object.keys(errors) as Array<keyof DemoElectronicContractBootstrapModel>)
        .map(name => ({ name, errors: [errors[name]!] })));
      return;
    }
    Modal.confirm({
      title: "Khởi tạo 12 mẫu demo?",
      content: `Tạo cấu hình 6 mẫu cá nhân và 6 mẫu tổ chức cho đơn vị hiện tại bằng profile ${values.connectionProfile}. Server xác minh kết nối demo và tài khoản ký trước khi lưu.`,
      okText: "Khởi tạo mẫu demo", cancelText: "Hủy",
      onOk: () => bootstrap(values),
    });
  };

  return <ConfigProvider theme={MODAL_THEME}>
    <Modal open={isModalOpen} title="Cấu hình mẫu hợp đồng điện tử" width={980}
      onCancel={() => !saving && setIsModalOpen(false)}
      footer={<Button onClick={() => setIsModalOpen(false)} disabled={saving}>Đóng</Button>}>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Alert type="info" showIcon message={`Cấu hình mẫu · ${isProduction ? "Production" : isUat ? "UAT / Demo" : "Chưa chọn môi trường"}`}
          description="Danh sách thuộc đơn vị và môi trường API đang đăng nhập. Mẫu cá nhân và tổ chức phải khớp người ký thực tế." />
        {error && <Alert type="error" showIcon message={error} />}
        {summary && <Alert type="success" showIcon message={summary} />}
        <Button onClick={() => { setError(""); void loadTemplates(); }} loading={loading} disabled={saving}>Tải lại danh sách</Button>
        <Table<ElectronicContractTemplateModel> size="small" rowKey="id" loading={loading} dataSource={templates} pagination={{ pageSize: 12 }} scroll={{ x: 700 }}
          locale={{ emptyText: "Chưa có cấu hình mẫu cho đơn vị này. Quản trị viên cần kiểm tra cấu hình trên server." }}
          columns={[
            { title: "Tên mẫu", dataIndex: "name", key: "name" },
            { title: "Chứng từ", key: "documentType", render: (_, row) => {
              const variant = getTemplateVariant(row);
              return variant ? documentTypeLabels[variant.documentType] || variant.documentType : "Chưa có cấu hình V2 hợp lệ";
            } },
            { title: "Khách hàng", key: "customerType", render: (_, row) => {
              const variant = getTemplateVariant(row);
              return variant ? <Tag>{variant.customerType === "CN" ? "CN · Cá nhân" : "DN · Tổ chức"}</Tag> : "—";
            } },
            { title: "ID mẫu Mắt Bão", dataIndex: "matBaoCATemplateId", key: "remoteId" },
            { title: "Trạng thái", key: "status", render: (_, row) => row.status === "Active"
              ? <Tag color="green">Đang hoạt động</Tag>
              : <Tag>{row.status === "Disabled" ? "Ngừng sử dụng" : row.status || "Chưa xác định"}</Tag> },
          ]} />
        {isAdmin && isUat && <>
          <Typography.Title level={5}>Khởi tạo 12 mẫu demo</Typography.Title>
          <Typography.Paragraph>Nhập profile đã được vận hành cấu hình trên server và hai tài khoản nội bộ đã xác minh của tenant demo. Thao tác chỉ lưu cấu hình mẫu trong WaterMS; không tạo hoặc ký hợp đồng.</Typography.Paragraph>
          <Form form={form} layout="vertical" onFinish={confirmBootstrap} disabled={saving}>
            <Form.Item name="connectionProfile" label="Tên profile kết nối demo" rules={[{ required: true, message: "Nhập tên profile trên server." }, { pattern: /^[A-Za-z0-9_-]{1,64}$/, message: "Dùng chữ, số, _ hoặc -, tối đa 64 ký tự." }]}>
              <Input maxLength={64} autoComplete="off" />
            </Form.Item>
            <Form.Item name="companyEmail" label="Email công ty (tài khoản API demo)" rules={[{ required: true, message: "Nhập email công ty." }, { type: "email", message: "Email không hợp lệ." }]}>
              <Input type="email" autoComplete="off" />
            </Form.Item>
            <Form.Item name="surveyEmail" label="Email nhân viên khảo sát nội bộ" dependencies={["companyEmail"]} rules={[{ required: true, message: "Nhập email nhân viên khảo sát." }, { type: "email", message: "Email không hợp lệ." }, ({ getFieldValue }) => ({ validator(_, value) {
              return value && value.trim().toLowerCase() === getFieldValue("companyEmail")?.trim().toLowerCase()
                ? Promise.reject(new Error("Email khảo sát phải khác tài khoản công ty.")) : Promise.resolve();
            } })]}>
              <Input type="email" autoComplete="off" />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={saving}>Khởi tạo 12 mẫu demo</Button>
          </Form>
        </>}
        {isProduction && <Alert type="warning" showIcon message="Production cần cấu hình mẫu và phương thức ký đã được xác minh riêng."
          description="Bộ mẫu demo không được dùng trên production. Các mẫu chưa xác minh SMS và chứng thư công ty cần giữ ở trạng thái ngừng sử dụng." />}
      </Space>
    </Modal>
  </ConfigProvider>;
};
